import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { digits } from "@/lib/brdocs";

export function ForgotPasswordDialog({ open, onOpenChange, initialEmail = "" }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setEmail(initialEmail); }, [open, initialEmail]);

  const close = (o) => {
    onOpenChange(o);
    if (!o) { setStep("email"); setCode(""); setPw({ a: "", b: "" }); }
  };

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      toast.success(data.msg);
      setStep("code");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("As senhas não coincidem");
    setBusy(true);
    try {
      await api.post("/auth/reset", { email, code, password: pw.a });
      toast.success("Senha redefinida! Entre com a nova senha.");
      close(false);
    } catch (err) { toast.error(formatApiError(err)); setCode(""); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>Redefinir senha</DialogTitle>
          <DialogDescription>{step === "email" ? "Digite o e-mail da sua conta para receber um código de 6 dígitos." : `Digite o código enviado para ${email} e escolha uma nova senha.`}</DialogDescription>
        </DialogHeader>
        {step === "email" ? (
          <form onSubmit={send} className="space-y-4">
            <div className="space-y-1.5"><Label>E-mail</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl bg-white/5" data-testid="forgot-email-input" /></div>
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full font-bold" data-testid="forgot-send-button">{busy ? "Enviando…" : "Enviar código"}</Button>
          </form>
        ) : (
          <form onSubmit={reset} className="space-y-4">
            <Input required value={code} onChange={(e) => setCode(digits(e.target.value).slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
              className="h-14 rounded-xl bg-white/5 text-center text-2xl font-extrabold tracking-[0.5em]" data-testid="forgot-code-input" />
            <div className="space-y-1.5"><Label>Nova senha</Label>
              <Input required type="password" minLength={6} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className="h-11 rounded-xl bg-white/5" data-testid="forgot-new-password-input" /></div>
            <div className="space-y-1.5"><Label>Confirmar nova senha</Label>
              <Input required type="password" minLength={6} value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className="h-11 rounded-xl bg-white/5" data-testid="forgot-confirm-password-input" /></div>
            <Button type="submit" disabled={busy || code.length !== 6} className="h-11 w-full rounded-full font-bold" data-testid="forgot-reset-button">{busy ? "Salvando…" : "Redefinir senha"}</Button>
            <button type="button" onClick={send} disabled={busy} className="w-full text-xs text-muted-foreground hover:text-white" data-testid="forgot-resend-button">Reenviar código</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
